import type { Directive, DirectiveBinding } from "vue";

/**
 * v-skeleton — masks an element with a loading placeholder.
 *
 * Annotate the real markup; there is no placeholder markup to build. While the
 * flag is on, the element is filled with the CA skeleton gradient and its
 * content stops being painted (but keeps its box, so the placeholder inherits
 * the natural size).
 *
 *   <h2 v-skeleton="loading">{{ title }}</h2>
 *   <picture v-skeleton="loading"><img :src="src" /></picture>
 *
 * A bare `v-skeleton` is always on, for elements that only exist while loading.
 *
 * An element whose content has not arrived collapses, so give the placeholder a
 * size: either with `skeleton-width` / `skeleton-height` / `skeleton-radius`
 * attributes (a bare number means px), or — when the size is responsive — from
 * the consumer's own CSS, via the `[data-ca-skeleton]` attribute set here.
 *
 * Recolour it by setting `--ca-skeleton-from` / `--ca-skeleton-to` on the
 * element or on any ancestor.
 *
 * Put it on the wrapper, never on an <img>: a replaced element paints its own
 * content over any background.
 */

/** Figma spec for the placeholder fill: solarized to solarized-dark. */
const FILL =
  "linear-gradient(270deg, var(--ca-skeleton-from, #F6F7F2) 0%, var(--ca-skeleton-to, #E5E3DF) 100%)";

/**
 * What it takes for the element to read as a plain box, whatever it was.
 * Hiding the children only covers child *elements*; text sitting directly
 * inside the element is painted by `color`, and the element's own decoration
 * would otherwise show straight through the placeholder.
 */
const FLATTEN = {
  background: FILL,
  color: "transparent",
  borderColor: "transparent",
  boxShadow: "none",
  filter: "none",
  animation: "none",
  pointerEvents: "none",
  userSelect: "none"
};

/** Where each masked element's own inline styles wait until it is unmasked. */
const saved = new WeakMap<HTMLElement, string | null>();

const length = (value: string) =>
  /^-?[\d.]+$/.test(value) ? `${value}px` : value;

const children = (el: HTMLElement) => Array.from(el.children) as HTMLElement[];

type SkeletonBinding = DirectiveBinding<boolean | undefined>;

function mask(el: HTMLElement, binding: SkeletonBinding) {
  if (binding.value !== undefined && !binding.value) return unmask(el);

  if (!saved.has(el)) saved.set(el, el.getAttribute("style"));

  const shape = (name: "width" | "height" | "radius") => {
    const value = el.getAttribute(`skeleton-${name}`);
    return value ? length(value) : null;
  };

  el.setAttribute("data-ca-skeleton", "");
  el.setAttribute("aria-busy", "true");

  const width = shape("width");
  const height = shape("height");
  const style = el.style;
  Object.assign(style, FLATTEN);
  style.borderRadius = shape("radius") ?? "8px";
  if (width) style.width = width;
  if (height) style.height = height;

  children(el).forEach(child => {
    child.style.visibility = "hidden";
  });
}

function unmask(el: HTMLElement) {
  if (!saved.has(el)) return;

  el.removeAttribute("data-ca-skeleton");
  el.removeAttribute("aria-busy");

  const style = saved.get(el) ?? null;
  if (style === null) el.removeAttribute("style");
  else el.setAttribute("style", style);

  children(el).forEach(child => {
    child.style.visibility = "";
  });

  saved.delete(el);
}

const vSkeleton: Directive<HTMLElement, boolean | undefined> = {
  mounted: mask,
  updated: mask,
  unmounted: unmask
};

export default vSkeleton;
